import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetBlueprintFamilyEventGroupTrendsQuery } from '../../../../api/blueprintFamilies/trendsEventGroups'
import { BlueprintFamilyTrendsPoint, BlueprintFamilyTrendsWindowDays } from '../../../../api/core'
import { TrendsChartSeriesItem, TrendsLineChartCard } from '../TrendsLineChartCard'
import { CatalogueMetricKey } from '../helpers/catalogueUtils'
import { selectedPeriodFor } from '../helpers/trendsSelectedPeriod'
import { MAX_TRENDS_SERIES, pickTrendsSeriesForMetric } from '../helpers/trendsSeries'

export type EventGroupTrendsChartGroup = TrendsChartSeriesItem

interface EventGroupTrendsChartProps {
  groups: EventGroupTrendsChartGroup[]
  // Which series to plot. Owned by the caller (the page's metric lens) so the
  // list beside the chart and the chart never disagree on what is shown.
  metric: CatalogueMetricKey
  windowDays?: BlueprintFamilyTrendsWindowDays
  conventionalSubgenreIds?: string[]
  isLoading?: boolean
  // Right-aligned header slot for the control that changes the plotted series.
  headerControls?: React.ReactNode
  // Rendered under the canvas, full card width.
  footer?: React.ReactNode
  // Overrides the default per-metric title (the detail pages name the level).
  title?: string
  // Hides the "Top N event groups in view" caption.
  hideSubtitle?: boolean
  // Full-width row above the title (metric lens tabs).
  tabs?: React.ReactNode
  // Render without the Card shell, for embedding inside another card.
  embedded?: boolean
  // Line color per group id; groups without one fall back to their position.
  colorById?: Record<string, string>
}

const TITLE_KEYS: Record<CatalogueMetricKey, string> = {
  adoption: 'market-analytics:event_group_trends_title',
  density: 'market-analytics:event_group_trends_density_title',
  activeEvents: 'market-analytics:event_group_trends_active_events_title',
}

/**
 * One line per event category (event group), fed by `trends/event-groups`,
 * whose series are deduplicated per game server-side. Presentation is
 * `TrendsLineChartCard`.
 */
export const EventGroupTrendsChart = React.memo(function EventGroupTrendsChart({
  groups,
  metric,
  windowDays,
  conventionalSubgenreIds,
  isLoading = false,
  headerControls,
  footer,
  title,
  hideSubtitle = false,
  tabs,
  embedded,
  colorById,
}: EventGroupTrendsChartProps) {
  const { t } = useTranslation()

  const topGroups = useMemo(() => groups.slice(0, MAX_TRENDS_SERIES), [groups])
  const eventGroupIds = useMemo(() => topGroups.map((group) => group.id), [topGroups])

  // Display-only: the response covers the selected period, and only its months are
  // plotted should it carry more.
  const selectedPeriod = useMemo(() => selectedPeriodFor(windowDays), [windowDays])

  // `currentData`, not `data`: these series are scoped to a segment and a window, and
  // `data` holds the previous arguments' response until the new one lands. Event-group
  // ids survive a segment change, so the old series would be plotted under the new
  // chip — the market's figures for a segment the user is no longer looking at. The
  // card is in its loading state throughout that gap (see `isLoading` below), so
  // dropping to undefined shows a skeleton rather than an empty chart.
  const { currentData, isFetching, isError, refetch } = useGetBlueprintFamilyEventGroupTrendsQuery(
    { eventGroupIds, windowDays, conventionalSubgenreIds },
    { skip: eventGroupIds.length === 0 }
  )

  const seriesById = useMemo(() => {
    const itemById = new Map((currentData ?? []).map((item) => [item.id, item]))
    const result: Record<string, BlueprintFamilyTrendsPoint[]> = {}
    topGroups.forEach((group) => {
      result[group.id] = pickTrendsSeriesForMetric(itemById.get(group.id), metric)
    })
    return result
  }, [currentData, topGroups, metric])

  return (
    <TrendsLineChartCard
      title={title ?? t(TITLE_KEYS[metric])}
      ariaLabel={t('market-analytics:event_group_trends_aria', { count: topGroups.length })}
      nameColumnHeader={t('market-analytics:event_group_trends_name_column')}
      items={topGroups}
      seriesById={seriesById}
      metric={metric}
      selectedPeriod={selectedPeriod ?? undefined}
      isLoading={isLoading || (eventGroupIds.length > 0 && isFetching)}
      isError={eventGroupIds.length > 0 && isError && !currentData}
      onRetry={refetch}
      subtitle={hideSubtitle ? undefined : t('market-analytics:event_group_trends_subtitle', { count: topGroups.length })}
      headerControls={headerControls}
      footer={footer}
      tabs={tabs}
      embedded={embedded}
      colorById={colorById}
    />
  )
})

EventGroupTrendsChart.displayName = 'EventGroupTrendsChart'
